import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { MutationConfig, QueryConfig } from '@/app/lib/tanstack-query';
import { createContact, deleteContact, getContact, getContacts, updateContact } from '@/app/network/api';

export const useCreateContact = ({ mutationConfig }: { mutationConfig?: MutationConfig<typeof createContact> } = {}) => {
  const queryClient = useQueryClient();

  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: getContactsQueryOptions().queryKey
      });

      onSuccess?.(...args);
    },
    ...restConfig,
    mutationFn: createContact
  });
};

export const useDeleteContact = ({ mutationConfig }: { mutationConfig?: MutationConfig<typeof deleteContact> } = {}) => {
  const { onSuccess, ...restConfig } = mutationConfig || {};
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: (data, variables, context) => {
      queryClient.removeQueries({
        queryKey: getContactQueryOptions(variables.contactId).queryKey
      });

      void queryClient.invalidateQueries({ queryKey: getContactsQueryOptions().queryKey });
      onSuccess?.(data, variables, context);
    },
    ...restConfig,
    mutationFn: deleteContact
  });
};

export function getContactQueryOptions(contactId: number) {
  return queryOptions({
    queryKey: ['contacts', contactId],
    queryFn: () => getContact({ contactId })
  });
}

export function useContact({ contactId, queryConfig }: { contactId: number; queryConfig?: QueryConfig<typeof getContactQueryOptions> }) {
  return useQuery({
    ...getContactQueryOptions(contactId),
    ...queryConfig
  });
}

export function getContactsQueryOptions() {
  return queryOptions({
    queryKey: ['contacts'],
    queryFn: () => getContacts()
  });
}

export function useContacts({ queryConfig }: { queryConfig?: QueryConfig<typeof getContactsQueryOptions> } = {}) {
  return useQuery({
    ...getContactsQueryOptions(),
    ...queryConfig
  });
}

export const useUpdateContact = ({ mutationConfig }: { mutationConfig?: MutationConfig<typeof updateContact> } = {}) => {
  const queryClient = useQueryClient();

  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries({
        queryKey: getContactsQueryOptions().queryKey
      });

      onSuccess?.(...args);
    },
    ...restConfig,
    mutationFn: updateContact
  });
};
